import React, { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle2, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { uploadWalletBalancesAPI, type UploadWalletResponse } from '../../../api/distributorWallet';

interface UploadWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccessRefresh: () => void;
}

export const UploadWalletModal: React.FC<UploadWalletModalProps> = ({
  isOpen,
  onClose,
  onSuccessRefresh,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadWalletResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (selectedFile: File | null) => {
    if (!selectedFile) return;
    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.csv') && !name.endsWith('.xls')) {
      setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      setFile(null);
      return;
    }
    setError(null);
    setFile(selectedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await uploadWalletBalancesAPI(file);
      setResult(res);
      onSuccessRefresh();
    } catch (err: any) {
      console.error('Wallet upload error:', err);
      setError(err.message || 'Failed to upload wallet balance file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError(null);
    setResult(null);
  };

  const formatCurrency = (val?: number) => {
    if (val === undefined || val === null) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(val);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-100">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
              <Upload className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">Upload Wallet Balances Sheet</h3>
              <p className="text-xs text-slate-500 font-medium">
                Batch update distributor current balances & credit limits via Excel/CSV
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Upload Drop Zone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-3xl p-8 text-center cursor-pointer transition-all ${
                  isDragOver
                    ? 'border-[#B71C1C] bg-red-50/50 scale-[0.99]'
                    : file
                    ? 'border-emerald-400 bg-emerald-50/40'
                    : 'border-slate-300 hover:border-slate-400 bg-slate-50/40'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                  className="hidden"
                />

                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <FileText className="w-6 h-6" />
                    </div>
                    <span className="text-sm font-bold text-slate-800">{file.name}</span>
                    <span className="text-xs text-slate-500 font-semibold">
                      {(file.size / 1024).toFixed(1)} KB — Click or drag another file to replace
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center mb-1">
                      <Upload className="w-6 h-6" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">
                      Drag & drop your wallet sheet here
                    </span>
                    <span className="text-xs text-slate-400 font-medium">
                      or click to browse files (.xlsx, .csv)
                    </span>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-2xl p-3 flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!file || loading}
                  className="px-6 py-2.5 rounded-xl bg-[#B71C1C] hover:bg-[#991515] text-white text-xs font-bold flex items-center gap-2 shadow-md shadow-red-900/10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Processing Upload...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Upload & Process Sheet</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Results View */
            <div className="space-y-6">
              {/* Response Status Message Banner */}
              {(result.data?.warningCount || 0) > 0 || (result.data?.failedCount || 0) > 0 ? (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-amber-900">
                      Import Processed with {result.data?.warningCount || 0} Warning(s) & {result.data?.failedCount || 0} Failure(s)
                    </h4>
                    <p className="text-xs text-amber-700 font-medium mt-0.5">{result.message}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <div>
                    <h4 className="text-xs font-bold text-emerald-900">Upload Processed Successfully</h4>
                    <p className="text-xs text-emerald-700">{result.message}</p>
                  </div>
                </div>
              )}

              {/* Summary Stats Grid */}
              {result.data && (
                <div className="grid grid-cols-4 gap-3 select-none">
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 text-center">
                    <span className="text-xs font-bold text-slate-500">Total Rows</span>
                    <p className="text-xl font-extrabold text-slate-800 mt-1">
                      {result.data.totalRows}
                    </p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 text-center">
                    <span className="text-xs font-bold text-emerald-600">Success</span>
                    <p className="text-xl font-extrabold text-emerald-700 mt-1">
                      {result.data.successCount}
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-center">
                    <span className="text-xs font-bold text-amber-600">Warnings</span>
                    <p className="text-xl font-extrabold text-amber-700 mt-1">
                      {result.data.warningCount}
                    </p>
                  </div>
                  <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3.5 text-center">
                    <span className="text-xs font-bold text-rose-600">Failed</span>
                    <p className="text-xl font-extrabold text-rose-700 mt-1">
                      {result.data.failedCount}
                    </p>
                  </div>
                </div>
              )}

              {/* Details Log Table */}
              {result.data?.details && result.data.details.length > 0 && (
                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
                  <div className="bg-slate-100 px-4 py-2.5 text-xs font-bold text-slate-700 border-b border-slate-200">
                    Itemized Row Processing Details ({result.data.details.length} rows)
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-slate-50 sticky top-0 font-bold text-slate-600 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2">SAP Code</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">New Balance</th>
                          <th className="px-3 py-2">Credit Limit</th>
                          <th className="px-3 py-2">Message / Remarks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        {result.data.details.map((detail, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/70">
                            <td className="px-3 py-2 font-bold text-slate-800">
                              {detail.sapCode || detail.sapCustomerId || '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  detail.status === 'SUCCESS'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : detail.status === 'WARNING'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-rose-100 text-rose-800'
                                }`}
                              >
                                {detail.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-emerald-700 font-bold">
                              {formatCurrency(detail.newBalance)}
                            </td>
                            <td className="px-3 py-2 font-semibold">
                              {formatCurrency(detail.newCreditLimit)}
                            </td>
                            <td className="px-3 py-2 font-semibold">
                              {detail.message ? (
                                <span
                                  className={
                                    detail.status === 'WARNING'
                                      ? 'text-amber-700 font-bold'
                                      : detail.status === 'FAILED'
                                      ? 'text-rose-700 font-bold'
                                      : 'text-slate-600'
                                  }
                                >
                                  {detail.message}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Reset / Close Buttons */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-[#B71C1C] hover:bg-red-50 transition-colors cursor-pointer"
                >
                  Upload Another File
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold cursor-pointer transition-colors shadow-md"
                >
                  Close Window
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadWalletModal;
